import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion, useInView, useScroll } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { spacing } from "css/spacing"
import { headline30 } from "css/text"

import Arrows from "./Arrows"
import { RollerProvider, useRoller } from "./context"
import P13N from "./perso"
import ActionCard from "./ActionCard"

const Container = styled(motion.section)`
  --dir: 1;
  &:dir(rtl) {
    --dir: -1;
  }

  ${fullGrid}
  grid-template-rows: [texts] auto [roller] auto [arrows] auto;

  grid-row: 5;
  grid-column: 1 / -1;

  overflow: hidden;
  padding-block: 80px;

  height: fit-content;

  div[class*="TextBlock"] {
    html.prefers-contrast & {
      z-index: 1;
    }
  }

  ${spacing}
  h2,
  h3,
  p {
    html.prefers-contrast & {
      color: black !important;
      position: relative;

      ::after {
        background: white;
        content: "";
        height: calc(100% + 30px);
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% + 20px);
        z-index: -1;
      }
    }
  }
`

const RollCont = styled.div`
  grid-column: doc;
  display: grid;
  align-items: center;
`

const List = styled.ul`
  display: grid;
  grid-auto-flow: column;
  gap: var(--grid-gap);

  grid-area: 1 / 1 / -1 / -1;

  overflow-x: auto;
  overflow-y: hidden;

  padding-inline-start: calc(var(--outer-margin));
  padding-inline-end: var(--outer-margin);

  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  --col-nb: 6;
  ${getMediaQuery("m")} {
    --col-nb: 8;
  }
  ${getMediaQuery("l")} {
    --col-nb: 6;
  }
  ${getMediaQuery("xl")} {
    --col-nb: 5;
  }
  ${getMediaQuery("xxl")} {
    --col-nb: 4;
  }

  grid-auto-columns: calc(var(--grid-col-unit) * var(--col-nb) + var(--grid-gap) * (var(--col-nb) - 1));

  & figure img {
    width: 100%;
    height: auto;
  }

  & li {
    scroll-snap-align: start;
    scroll-margin-inline-start: calc(var(--outer-margin));
  }

  // to prevent cards focus outline to be cut :
  padding-block-start: 10px;
  margin-block-start: -10px;
  padding-block-end: 10px;
  margin-block-end: -10px;
  //////////////////////////////////////////////
`

const RollerTitle = styled.h2`
  ${headline30}
  z-index: 1;
  color: white;
  grid-column: 2 / span 3;
  margin-block-end: 20px;
`

function Strip({ labelledBy, items }, ref) {
  const console = useConsole()

  const ractives = useRef(new Set())

  const { max, unit, isTouchDevice } = useRoller()
  const { scrollX } = useScroll({ container: ref })

  const [showArrows, setShowArrows] = useState(false)
  const isInView = useInView(ref)

  function updateUnit() {
    max.set(ref.current.scrollWidth - ref.current.clientWidth - unit.get() / 2)
    setShowArrows(max.get() > 0)
  }

  const onWchange = () => updateUnit()
  useEffect(() => unit.onChange(onWchange))

  function onFocusCapture(e) {
    const curIndex = Number(e.target.getAttribute("data-index"))

    if (!ractives.current.has(curIndex) && e.target.classList.contains("focus-visible")) {
      const pofs = ref.current.children[0].offsetLeft
      const ofs = e.target.offsetLeft

      ref.current.scrollTo({
        top: 0,
        left: ofs - pofs,
        behavior: "smooth",
      })
    }
  }

  useLayoutEffect(() => {
    const r = ref.current
    const elements = Array.prototype.slice.call(r.children)
    const actives = ractives.current

    if (r.children.length) {
      unit.set(r.children[0]?.offsetWidth)
    }

    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          const index = elements.indexOf(entry.target)
          if (entry.intersectionRect.width / entry.boundingClientRect.width === 1) actives.add(index)
          else actives.delete(index)
        }),
      {
        threshold: 1,
      }
    )

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize && r.children.length) {
          unit.set(r.children[0].offsetWidth)
        }
      }
    })

    resizeObserver.observe(r)
    elements.forEach(element => observer.observe(element))

    return () => {
      resizeObserver.unobserve(r)
      elements.forEach(element => observer.unobserve(element))
    }
  }, [])

  useLayoutEffect(() => {
    updateUnit()
  }, [])

  useEffect(() => {
    !isInView &&
      ref.current.scrollTo({
        top: 0,
        left: 0,
      })
  }, [isInView])

  return (
    <RollCont>
      <List ref={ref} aria-labelledby={labelledBy} onFocusCapture={onFocusCapture}>
        {items.map(({ item, item_p13n }, i) => {
          return item_p13n ? (
            <li key={item_p13n._metadata.uid}>
              <P13N {...item_p13n} uid={item_p13n._metadata.uid} index={i} />
            </li>
          ) : item ? (
            <li key={item._metadata.uid}>
              <ActionCard {...item.card[0]} index={i} />
            </li>
          ) : null
        })}
      </List>
      {showArrows && !isTouchDevice ? <Arrows ref={ref} scrollX={scrollX} /> : null}
    </RollCont>
  )
}

const StripWithRef = forwardRef(Strip)

export default function Roller({ roller_title, texts, items, variants }) {
  const console = useConsole()

  const ref = useRef()

  const isInView = useInView(ref, { once: true })

  const labelledBy = texts?.length ? texts.find(o => "heading" in o)?.heading?._metadata.uid : null

  return (
    <RollerProvider>
      <Container className={`${isInView ? " show" : ""}`} variants={variants}>
        {roller_title ? <RollerTitle>{roller_title}</RollerTitle> : null}
        <StripWithRef ref={ref} labelledBy={labelledBy} items={items} />
      </Container>
    </RollerProvider>
  )
}
